/**
 * Main chat hook for HIVA WebChat Widget
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { WebChatAPI } from '../services/api';
import { WebChatWebSocket } from '../services/websocket';
import type { WidgetConfig, WidgetState, Message, VisitorInfo } from '../types';

export function useChat(config: WidgetConfig) {
  const [state, setState] = useState<WidgetState>({
    isOpen: false,
    isMinimized: true,
    conversationId: null,
    sessionToken: null,
    messages: [],
    isConnected: false,
    isTyping: false,
    agentName: null,
  });

  const apiRef = useRef<WebChatAPI | null>(null);
  const wsRef = useRef<WebChatWebSocket | null>(null);
  const welcomeMessageShown = useRef<boolean>(false);

  // Initialize API client
  useEffect(() => {
    apiRef.current = new WebChatAPI(config.apiUrl, config.integrationId);
  }, [config.apiUrl, config.integrationId]);

  // Show welcome message locally when widget opens (without creating conversation)
  useEffect(() => {
    if (state.isOpen && !state.isMinimized && !welcomeMessageShown.current) {
      welcomeMessageShown.current = true;
      
      // Add welcome message locally (not saved to DB)
      const welcomeMessage = config.theme?.welcomeMessage || '¡Hola! ¿En qué podemos ayudarte?';
      const welcomeMsg: Message = {
        id: crypto.randomUUID(),
        conversation_id: 'local-preview',
        content: welcomeMessage,
        sender_type: 'SYSTEM',
        created_at: new Date().toISOString(),
        is_read: false,
      };
      
      setState((prev) => ({
        ...prev,
        messages: [welcomeMsg],
        agentName: 'Asistente Virtual', // Default name until conversation starts
      }));
    }
  }, [state.isOpen, state.isMinimized, config.theme?.welcomeMessage]);

  // Connect to WebSocket (defined before startConversation to avoid hoisting issues)
  const connectWebSocket = useCallback((url: string): Promise<void> => {
    return new Promise((resolve) => {
      if (wsRef.current) {
        wsRef.current.disconnect();
      }

      wsRef.current = new WebChatWebSocket(url);

      // Set up event handlers
      wsRef.current.onConnect(() => {
        setState((prev) => ({ ...prev, isConnected: true }));
        resolve(); // Resolve promise when connected
      });

      wsRef.current.onDisconnect(() => {
        setState((prev) => ({ ...prev, isConnected: false }));
      });

      wsRef.current.onMessage((message) => {
        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, message],
          isTyping: false,
        }));
      });

      wsRef.current.onTyping((isTyping) => {
        setState((prev) => ({ ...prev, isTyping }));
      });

      // Connect
      wsRef.current.connect();
    });
  }, []);

  // Start conversation (called lazily when user sends first message)
  const startConversation = useCallback(async (initialMessage?: string) => {
    if (!apiRef.current) return;

    try {
      // Gather visitor info
      const visitorInfo: VisitorInfo = {
        page_url: window.location.href,
        page_title: document.title,
        user_agent: navigator.userAgent,
        referrer: document.referrer || undefined,
      };

      console.log('[WebChat] Starting conversation with initial message:', initialMessage ? 'yes' : 'no');

      // Start conversation (with initial_message if provided)
      const response = await apiRef.current.startConversation(visitorInfo, initialMessage);

      // Update state with conversation info
      setState((prev) => ({
        ...prev,
        conversationId: response.conversation_id,
        sessionToken: response.session_token,
        agentName: response.agent_name || prev.agentName,
      }));

      // Connect to WebSocket and wait for connection
      if (response.websocket_url) {
        console.log('[WebChat] Connecting to WebSocket...');
        await connectWebSocket(response.websocket_url);
        console.log('[WebChat] WebSocket connected, ready to receive messages');
      } else {
        console.error('[WebChat] No websocket_url in response!');
      }

      // NOTE: Welcome message is already shown locally when widget opens
      // No need to add it again here - agent response will come via WebSocket

      return response;
    } catch (error) {
      console.error('[WebChat] Error starting conversation:', error);
      throw error;
    }
  }, [connectWebSocket]);

  // Send message (creates conversation on first message - LAZY CONVERSATION)
  const sendMessage = useCallback(async (content: string) => {
    if (!apiRef.current) {
      throw new Error('API not initialized');
    }

    try {
      // Add user message to state immediately (optimistic update) and show typing indicator
      const userMessage: Message = {
        id: crypto.randomUUID(),
        conversation_id: state.conversationId || 'pending',
        content,
        sender_type: 'USER',
        created_at: new Date().toISOString(),
        is_read: false,
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        isTyping: true, // Show typing indicator while waiting for agent response
      }));

      // LAZY CONVERSATION: If no conversation exists, create one first (without initial_message)
      // Then send the message through the normal endpoint after WebSocket is connected
      if (!state.conversationId || !state.sessionToken) {
        console.log('[WebChat] Creating conversation first (lazy), then sending message...');
        
        // Step 1: Create conversation WITHOUT initial_message
        const response = await startConversation();
        
        if (!response) {
          throw new Error('Failed to start conversation');
        }
        
        // Step 2: Now send the message through the normal endpoint
        // WebSocket is already connected at this point
        console.log('[WebChat] Conversation created, now sending message...');
        const sendResponse = await apiRef.current.sendMessage(response.conversation_id, response.session_token, {
          content,
          type: 'text',
        });
        
        // If human takeover, turn off typing indicator (no AI will respond)
        if (sendResponse?.is_human_takeover) {
          setState((prev) => ({ ...prev, isTyping: false }));
        }
        
        // Agent response will come via WebSocket
        return;
      }

      // Send to API (conversation already exists)
      try {
        const sendResponse = await apiRef.current.sendMessage(state.conversationId, state.sessionToken, {
          content,
          type: 'text',
        });
        
        // If human takeover, turn off typing indicator (no AI will respond)
        if (sendResponse?.is_human_takeover) {
          setState((prev) => ({ ...prev, isTyping: false }));
        }
      } catch (error: any) {
        // If 401 Unauthorized, session expired - restart conversation automatically
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          console.log('[WebChat] Session expired, restarting conversation...');
          
          // Start new session, then send message
          const response = await startConversation();
          if (response) {
            await apiRef.current.sendMessage(response.conversation_id, response.session_token, {
              content,
              type: 'text',
            });
          }
          
          return;
        }
        
        // Other errors, rethrow
        throw error;
      }

      // Agent response will come via WebSocket
    } catch (error) {
      console.error('[WebChat] Error sending message:', error);
      throw error;
    }
  }, [state.conversationId, state.sessionToken, startConversation]);

  // Toggle widget
  const toggleWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      isMinimized: prev.isOpen ? true : false,
    }));
  }, []);

  // Open widget (idempotent - safe to call multiple times)
  const openWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: true,
      isMinimized: false,
    }));
  }, []);

  // Close widget (idempotent)
  const closeWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: true,
      isMinimized: true,
    }));
  }, []);

  // Minimize widget
  const minimizeWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isMinimized: true,
    }));
  }, []);

  // Maximize widget
  const maximizeWidget = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isMinimized: false,
    }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.disconnect();
      }
    };
  }, []);

  return {
    state,
    startConversation,
    sendMessage,
    toggleWidget,
    openWidget,
    closeWidget,
    minimizeWidget,
    maximizeWidget,
  };
}
